/* Shared helpers — monthName / quarter: client-side on most tabs; sent to API on bridge/LTM only */

import { assignApiFilter, toReportApiFilters, stripFilterSentinels } from "../filterRelationshipUtils";

export const QUARTER_MONTHS = {
  Q1: ["jan", "feb", "mar"],
  Q2: ["apr", "may", "jun"],
  Q3: ["jul", "aug", "sep"],
  Q4: ["oct", "nov", "dec"],
};

export const MONTH_INDEX = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

export const ALL_MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
export const ALL_QS = ["Q1", "Q2", "Q3", "Q4"];

const FULL_MONTH_NAMES = {
  january: "jan", february: "feb", march: "mar", april: "apr",
  may: "may", june: "jun", july: "jul", august: "aug",
  september: "sep", october: "oct", november: "nov", december: "dec",
};

const MONTH_LABELS = {
  jan: "Jan", feb: "Feb", mar: "Mar", apr: "Apr", may: "May", jun: "Jun",
  jul: "Jul", aug: "Aug", sep: "Sep", oct: "Oct", nov: "Nov", dec: "Dec",
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatLtmMonthYear(monthIndex0, year) {
  return `${MONTH_NAMES[monthIndex0]}-${year}`;
}

/**
 * Add monthName/quarter to an API payload.
 * Quarter is omitted when a month is selected (server ignores it on LTM-style endpoints).
 */
export function appendPeriodFilters(target, filters) {
  if (!target || !filters) return target;
  const monthPayload = {};
  assignApiFilter(monthPayload, "monthName", filters.monthName);
  const quarterPayload = {};
  assignApiFilter(quarterPayload, "quarter", filters.quarter);
  const monthKeys = (monthPayload.monthName || [])
    .map(monthNameToKey)
    .filter(Boolean);
  const hasMonth = monthKeys.length > 0;
  if ("monthName" in monthPayload) target.monthName = monthPayload.monthName;
  if ("quarter" in quarterPayload && !hasMonth) target.quarter = quarterPayload.quarter;
  return target;
}

/**
 * LTM KPI request filters — quarter narrows full year only when no month is selected.
 */
export function buildLtmKpiApiFilters(filters) {
  if (!filters) return {};
  const monthPayload = {};
  assignApiFilter(monthPayload, "monthName", filters.monthName);
  const hasMonth = (monthPayload.monthName || [])
    .map(monthNameToKey)
    .filter(Boolean).length > 0;

  const out = toReportApiFilters(filters);
  if (hasMonth) delete out.quarter;
  return out;
}

/** Period banner from API windowStart/windowEnd (YYYY-MM). */
export function formatLtmKpiPeriodFromWindow(windowStart, windowEnd) {
  if (!windowStart || !windowEnd) return null;
  const fmtYm = (ym) => {
    const [y, m] = String(ym).split("-");
    const idx = Number(m) - 1;
    if (!y || idx < 0 || idx > 11) return ym;
    return formatLtmMonthYear(idx, Number(y));
  };
  const startLabel = fmtYm(windowStart);
  const endLabel = fmtYm(windowEnd);
  if (windowStart === windowEnd) return `LTM KPI (${startLabel})`;
  return `LTM KPI (${startLabel} to ${endLabel})`;
}

/**
 * LTM KPI section banner fallback from global year + monthName/quarter filters.
 * Month + ltmMode "single" → that month only; month + "ltm" (default) → trailing 12;
 * no month + quarter → quarter range; else full year.
 */
export function formatLtmKpiPeriodLabel(filters, ltmMode = "ltm") {
  const year = filters?.year && String(filters.year) !== "All"
    ? Number(filters.year)
    : new Date().getFullYear();

  const monthKeys = (filters?.monthName || [])
    .map(monthNameToKey)
    .filter(Boolean);

  if (monthKeys.length >= 1) {
    const endKey = monthKeys.reduce((a, b) =>
      (MONTH_INDEX[a] || 0) >= (MONTH_INDEX[b] || 0) ? a : b,
    );
    const endIdx = MONTH_INDEX[endKey] - 1;
    if (String(ltmMode).toLowerCase() === "single") {
      return `LTM KPI (${formatLtmMonthYear(endIdx, year)})`;
    }
    const end = new Date(year, endIdx, 1);
    const start = new Date(year, endIdx - 11, 1);
    return `LTM KPI (${formatLtmMonthYear(start.getMonth(), start.getFullYear())} to ${formatLtmMonthYear(end.getMonth(), end.getFullYear())})`;
  }

  const quarters = (filters?.quarter || [])
    .map(normalizeQuarter)
    .filter((q) => ALL_QS.includes(q));
  if (quarters.length === 1) {
    const months = QUARTER_MONTHS[quarters[0]];
    const startIdx = MONTH_INDEX[months[0]] - 1;
    const endIdx = MONTH_INDEX[months[months.length - 1]] - 1;
    return `LTM KPI (${formatLtmMonthYear(startIdx, year)} to ${formatLtmMonthYear(endIdx, year)})`;
  }

  return `LTM KPI (${formatLtmMonthYear(0, year)} to ${formatLtmMonthYear(11, year)})`;
}

/** Live calendar year for "Actuals through" — never follows the Year global filter. */
export function liveActualsYear() {
  return new Date().getFullYear();
}

/** e.g. "Actuals through May 2026" — year is always the live calendar year */
export function formatActualsThroughDate(cutoffMonth, _fiscalYearIgnored) {
  const m = Number(cutoffMonth);
  if (!m || m < 1 || m > 12) return null;
  return `Actuals through ${MONTH_NAMES[m - 1]} ${liveActualsYear()}`;
}

/**
 * "Actuals through …" derived from an `actualMonths` array (jan..dec keys, any case).
 * When windowStart/windowEnd ("YYYY-MM") are given, walks backward from windowEnd to
 * windowStart so a window spanning two calendar years (e.g. LTM) resolves the true
 * chronological latest month instead of the highest Jan-Dec calendar index.
 * Without a window, falls back to plain calendar-order latest-present.
 * Returns null when there are no actual months (e.g. a future fiscal year).
 */
export function formatActualsThroughFromMonths(actualMonths, windowStart, windowEnd) {
  if (!actualMonths?.length) return null;
  const actualSet = new Set(actualMonths.map((m) => String(m).toLowerCase()));

  if (windowStart && windowEnd) {
    let [y, m] = String(windowEnd).split("-").map(Number);
    if (y && m) {
      for (let i = 0; i < 12; i++) {
        const key = ALL_MONTHS[m - 1];
        if (key && actualSet.has(key)) return formatActualsThroughDate(m);
        const ym = `${y}-${String(m).padStart(2, "0")}`;
        if (ym === windowStart) break;
        m -= 1;
        if (m < 1) { m = 12; y -= 1; }
      }
      return null;
    }
  }

  const found = [...ALL_MONTHS].reverse().find((k) => actualSet.has(k));
  return found ? formatActualsThroughDate(MONTH_INDEX[found]) : null;
}

/** Hide actuals-through label when viewing a future fiscal year */
export function isFutureFiscalYear(year) {
  const selected = year && String(year) !== "All" ? Number(year) : new Date().getFullYear();
  return selected > new Date().getFullYear();
}

/**
 * Live "Actuals through …" cutoff (previous calendar month).
 * Does not follow the Year global filter.
 */
export function deriveActualsThroughFromYear(_yearFilterIgnored) {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `Actuals through ${MONTH_NAMES[prev.getMonth()]} ${prev.getFullYear()}`;
}

/**
 * Page header "Actuals through …" date.
 * Returns null while loading or for future FY; otherwise first API-derived candidate.
 */
export function headerActualsDate(yearFilter, loading, ...candidates) {
  if (loading || isFutureFiscalYear(yearFilter)) return null;
  return candidates.find((c) => c) ?? null;
}

/** When API returns a month label (e.g. "May") instead of 1–12 */
export function formatActualsThroughFromLabel(monthLabel, _fiscalYearIgnored) {
  if (!monthLabel) return null;
  return `Actuals through ${monthLabel} ${liveActualsYear()}`;
}

export function resolveActualsThroughDate(data, _fallbackYearIgnored) {
  if (!data) return null;
  const month = data.actualsCutoffMonth ?? data.actualThroughMonth;
  return formatActualsThroughDate(month);
}

export function monthNameToKey(name) {
  if (!name) return null;
  const lower = String(name).trim().toLowerCase();
  if (MONTH_INDEX[lower]) return lower;
  const abbr = lower.slice(0, 3);
  if (MONTH_INDEX[abbr]) return abbr;
  return FULL_MONTH_NAMES[lower] || null;
}

export function normalizeQuarter(q) {
  const s = String(q).trim().toUpperCase();
  if (!s) return null;
  return s.startsWith("Q") ? s : `Q${s}`;
}

export function sumMonths(ds, months) {
  if (!ds) return 0;
  return months.reduce((acc, m) => acc + (ds[m] || 0), 0);
}

export function getWindowMonths(timelines, monthKeys) {
  if (monthKeys?.length) return monthKeys;
  if (!timelines || timelines.length === 0) return ALL_MONTHS;
  const months = [];
  timelines.forEach((q) => {
    (QUARTER_MONTHS[q] || []).forEach((m) => { if (!months.includes(m)) months.push(m); });
  });
  return months.length ? months : ALL_MONTHS;
}

export function getWindowTotal(ds, timelines, monthKeys) {
  if (!ds) return 0;
  if (monthKeys?.length) return sumMonths(ds, monthKeys);
  if (!timelines || timelines.length === 0) return ds.total || 0;
  return timelines.reduce((acc, q) => acc + (ds[q.toLowerCase()] || 0), 0);
}

export function deriveTimeWindowFromFilters(filters) {
  const quarters = stripFilterSentinels(filters?.quarter)
    .map(normalizeQuarter)
    .filter((q) => ALL_QS.includes(q));
  const monthKeys = stripFilterSentinels(filters?.monthName)
    .map(monthNameToKey)
    .filter(Boolean);

  let timeline = [];
  if (quarters.length) {
    timeline = quarters;
  } else if (monthKeys.length) {
    const qs = new Set();
    monthKeys.forEach((m) => {
      Object.entries(QUARTER_MONTHS).forEach(([q, months]) => {
        if (months.includes(m)) qs.add(q);
      });
    });
    timeline = ALL_QS.filter((q) => qs.has(q));
  }

  let effectiveMonthKeys = null;
  if (monthKeys.length) {
    const allowed = quarters.length ? getWindowMonths(quarters) : ALL_MONTHS;
    const narrowed = monthKeys.filter((m) => allowed.includes(m));
    effectiveMonthKeys = narrowed.length ? narrowed : monthKeys;
  }

  return { timeline, monthKeys: effectiveMonthKeys };
}

/** Serialize non-time API filters for stable useEffect dependency */
export function getApiFilterKey(filters, buildApiFilters) {
  return JSON.stringify(buildApiFilters(filters));
}

/**
 * Period column keys (jan-dec or q1-q4) implied by global filters.
 * Returns null when no time filter is active (show all periods).
 */
export function getGlobalPeriodColumnKeys(filters, viewMode = "Month") {
  const { timeline, monthKeys } = deriveTimeWindowFromFilters(filters);
  const isQuarter = viewMode === "Quarter";

  if (isQuarter) {
    if (!timeline.length) return null;
    return timeline.map((q) => q.toLowerCase());
  }

  if (monthKeys?.length) return monthKeys;
  if (timeline.length) return getWindowMonths(timeline);
  return null;
}

/** Chart/table labels for global period filter (Jan, Feb… or Q1, Q2…) */
export function getGlobalPeriodLabels(filters, viewMode = "Month") {
  const keys = getGlobalPeriodColumnKeys(filters, viewMode);
  if (!keys) return null;
  if (viewMode === "Quarter") return keys.map((k) => k.toUpperCase());
  return keys.map((k) => MONTH_LABELS[k] || k);
}

/** Keys never hidden by global month/quarter filter */
const PERIOD_PRESERVED_COL_KEYS = new Set(["total", "ytd"]);

export function sumPeriodKeys(rowData, periodKeys) {
  if (!rowData || !periodKeys?.length) return 0;
  return periodKeys.reduce((acc, k) => acc + (Number(rowData[k]) || 0), 0);
}

/** Actual months within the selected period (excludes forecast months). */
function getYtdMonthsInPeriod(periodKeys, actualMonthKeys) {
  if (!periodKeys?.length) return [];
  const actualSet =
    actualMonthKeys instanceof Set ? actualMonthKeys : new Set(actualMonthKeys || []);
  return periodKeys.filter((k) => actualSet.has(k));
}

/** YTD column value for the selected month/quarter window (client-side). */
export function getPeriodFilteredYtd(rowData, filters, viewMode = "Month", options = {}) {
  const periodKeys = getGlobalPeriodColumnKeys(filters, viewMode);
  if (!periodKeys?.length) return rowData?.ytd ?? 0;

  const { actualMonthKeys, rowKey, allRowData, isPct = false } = options;
  const ytdMonths = getYtdMonthsInPeriod(periodKeys, actualMonthKeys);
  if (!ytdMonths.length) return 0;

  if (isPct && allRowData) {
    if (rowKey === "grossMarginPct") {
      const rev = sumPeriodKeys(allRowData.contractRevenue, ytdMonths);
      const gm = sumPeriodKeys(allRowData.grossMargin, ytdMonths);
      return rev ? (gm / rev) * 100 : 0;
    }
    if (rowKey === "netMarginPct") {
      const rev = sumPeriodKeys(allRowData.contractRevenue, ytdMonths);
      const np = sumPeriodKeys(allRowData.netProfit, ytdMonths);
      return rev ? (np / rev) * 100 : 0;
    }
  }

  return sumPeriodKeys(rowData, ytdMonths);
}

/** Total column value for the selected month/quarter window (client-side). */
export function getPeriodFilteredTotal(rowData, filters, viewMode = "Month", options = {}) {
  const periodKeys = getGlobalPeriodColumnKeys(filters, viewMode);
  if (!periodKeys?.length) return rowData?.total ?? 0;

  const { rowKey, allRowData, isPct = false } = options;

  if (isPct && allRowData) {
    if (rowKey === "grossMarginPct") {
      const rev = sumPeriodKeys(allRowData.contractRevenue, periodKeys);
      const gm = sumPeriodKeys(allRowData.grossMargin, periodKeys);
      return rev ? (gm / rev) * 100 : 0;
    }
    if (rowKey === "netMarginPct") {
      const rev = sumPeriodKeys(allRowData.contractRevenue, periodKeys);
      const np = sumPeriodKeys(allRowData.netProfit, periodKeys);
      return rev ? (np / rev) * 100 : 0;
    }
  }

  return sumPeriodKeys(rowData, periodKeys);
}

/** Resolve a table cell value; adjusts total and YTD for period filter. */
export function resolvePeriodCellValue(rowData, colKey, filters, viewMode = "Month", options = {}) {
  const data = rowData || {};
  if (colKey === "ytd") {
    const { varianceSources, rowKey, allRowData, isPct, actualMonthKeys } = options;
    const compareSource = varianceSources?.comparison ?? varianceSources?.budget;
    if (varianceSources?.live && compareSource && rowKey) {
      const liveY = getPeriodFilteredYtd(
        varianceSources.live[rowKey],
        filters,
        viewMode,
        { rowKey, allRowData: varianceSources.live, isPct, actualMonthKeys },
      );
      const compActual = varianceSources.compareActualMonthKeys ?? actualMonthKeys;
      const compY = getPeriodFilteredYtd(
        compareSource[rowKey],
        filters,
        viewMode,
        { rowKey, allRowData: compareSource, isPct, actualMonthKeys: compActual },
      );
      return liveY - compY;
    }
    return getPeriodFilteredYtd(data, filters, viewMode, {
      rowKey,
      allRowData,
      isPct,
      actualMonthKeys,
    });
  }

  if (colKey === "total") {
    const { varianceSources, rowKey, allRowData, isPct } = options;
    const compareSource = varianceSources?.comparison ?? varianceSources?.budget;
    if (varianceSources?.live && compareSource && rowKey) {
      const liveT = getPeriodFilteredTotal(
        varianceSources.live[rowKey],
        filters,
        viewMode,
        { rowKey, allRowData: varianceSources.live, isPct },
      );
      const compT = getPeriodFilteredTotal(
        compareSource[rowKey],
        filters,
        viewMode,
        { rowKey, allRowData: compareSource, isPct },
      );
      return liveT - compT;
    }
    return getPeriodFilteredTotal(data, filters, viewMode, {
      rowKey: options.rowKey,
      allRowData: options.allRowData,
      isPct,
    });
  }

  return data[colKey] ?? 0;
}

/** Keys to hide in column pickers when global time filter is active */
export function getGlobalPeriodHiddenKeys(filters, viewMode, allDataKeys) {
  const allowed = getGlobalPeriodColumnKeys(filters, viewMode);
  if (!allowed) return new Set();
  const allowedSet = new Set(allowed);
  return new Set(
    allDataKeys.filter((k) => !PERIOD_PRESERVED_COL_KEYS.has(k) && !allowedSet.has(k)),
  );
}
